import React, { useCallback, useEffect, useMemo } from 'react';
import {
  ContextualMenu,
  ContextualMenuItemType,
  DirectionalHint,
  HighContrastSelector,
  IContextualMenuItem,
  memoizeFunction,
  mergeStyleSets,
  Stack,
} from '@fluentui/react';
import { Premium24Regular as PremiumIcon } from '@fluentui/react-icons';
import { useAuth } from 'AuthProvider';
import { IAppTheme, Text, useAppTheme } from 'common';
import { logClick } from 'common/utilities/telemetryHelpers';
import { ManageOrCancelSubscriptionURL, MicrosoftAccountURL } from 'common/utilities/URLConstants';
import { shouldMeetFrenchRegulation, useLocalizedStrings } from 'i18n';
import { showOfficeShellPanel, useAppDispatch } from 'store';

import { ocvFeedbackLoadAndInitialize, renderFeedbackForm } from './OcvFeedback';
import { openHelpFlexPane } from './OfficeShell';

export type MenuItemKey =
  | 'Settings_Notification'
  | 'Settings_FamilySharing'
  | 'Privacy'
  | 'Subscription'
  | 'Help'
  | 'Feedback_Bug'
  | 'Feedback_Like'
  | 'Feedback_Suggestion'
  | '_Divider'
  | false;

interface IOfficeShellMenuProps {
  menuButtonId?: string;
  menuItemKeys?: MenuItemKey[];
  hidden: boolean;
  onHide: () => void;
}

type MenuItem = IContextualMenuItem & {
  key: MenuItemKey;
};

const getStyles = memoizeFunction((theme: IAppTheme) =>
  mergeStyleSets({
    subscriptionMenuItem: {
      margin: '0 4px',
    },
    subscriptionIcon: {
      marginRight: 5,

      [HighContrastSelector]: {
        color: theme.semanticColors.bodyText,
      },
    },
    subscriptionMyCurrentPlanText: {
      lineHeight: 14,
      paddingTop: 10,
      color: theme.semanticColors.bodyText,
    },
    subscriptionInfoText: {
      color: theme.semanticColors.bodyText,
    },
  })
);

const menuItemClassNames = mergeStyleSets({
  menuItem: {
    height: 'auto',
  },
});

const buildMenuItem = (
  key: Exclude<MenuItemKey, false>,
  textOrOnRenderContent: string | IContextualMenuItem['onRenderContent'],
  onClick: () => void
): MenuItem => ({
  key,
  ...(typeof textOrOnRenderContent === 'string' ? { text: textOrOnRenderContent } : { onRenderContent: textOrOnRenderContent }),
  itemProps: {
    styles: {
      root: menuItemClassNames.menuItem,
    },
  },
  onClick: logClick(`OfficeShellMenu_Click_${key}`, onClick),
});

export const OfficeShellMenu: React.FC<IOfficeShellMenuProps> = (props: IOfficeShellMenuProps) => {
  const { menuButtonId, menuItemKeys, hidden, onHide } = props;
  const dispatch = useAppDispatch();
  const theme = useAppTheme();
  const classNames = getStyles(theme);

  useEffect(() => {
    ocvFeedbackLoadAndInitialize();
  }, []);

  const { t } = useLocalizedStrings();
  const { license } = useAuth();

  const subscriptionLink = shouldMeetFrenchRegulation() ? ManageOrCancelSubscriptionURL : MicrosoftAccountURL;
  const subscriptionText = shouldMeetFrenchRegulation()
    ? t('OfficeShellMenu.ManageOrCancelSubscription')
    : t('OfficeShellMenu.MyCurrentPlan');

  const renderSubscriptionMenuItem = useCallback(
    () => (
      <Stack className={classNames.subscriptionMenuItem} verticalAlign="center">
        <Text variant="smallPlus" className={classNames.subscriptionMyCurrentPlanText}>
          {subscriptionText}
        </Text>
        <Stack horizontal verticalAlign="center">
          <PremiumIcon color={theme.palette.themePrimary} className={classNames.subscriptionIcon} />
          <Text className={classNames.subscriptionInfoText}>
            {
              // i18next-extract-mark-context-start ["Family", "Personal"]
              t('OfficeShellMenu.Subscription', {
                context: license?.activeSubscriptionProduct?.isFamily ? 'Family' : 'Personal',
              })
              // i18next-extract-mark-context-stop
            }
          </Text>
        </Stack>
      </Stack>
    ),
    [
      license?.activeSubscriptionProduct?.isFamily,
      theme.palette.themePrimary,
      subscriptionText,
      classNames.subscriptionMenuItem,
      classNames.subscriptionMyCurrentPlanText,
      classNames.subscriptionIcon,
      classNames.subscriptionInfoText,
      t,
    ]
  );

  const menuItems: MenuItem[] = useMemo(
    () => [
      // Notification
      buildMenuItem(
        'Settings_Notification',
        t('OfficeShellMenu.Settings_Notification'),
        logClick('NotificationSettingsPanelOpen', () => dispatch(showOfficeShellPanel({ panelType: 'NotificationSettings' })))
      ),

      // Family sharing
      buildMenuItem(
        'Settings_FamilySharing',
        t('OfficeShellMenu.Settings_FamilySharing'),
        logClick('FamilyDialogOpen', () => dispatch(showOfficeShellPanel({ panelType: 'FamilySettings' })))
      ),

      // Privacy
      buildMenuItem(
        'Privacy',
        t('OfficeShellMenu.Privacy'),
        logClick('DataPrivacyMenu', () => {
          dispatch(showOfficeShellPanel({ panelType: 'PrivacySettings' }));
        })
      ),

      // Subscription
      buildMenuItem('Subscription', renderSubscriptionMenuItem, () => window.open(subscriptionLink)),

      // Help
      buildMenuItem(
        'Help',
        t('OfficeShellMenu.Help'),
        logClick('GetHelp', () => openHelpFlexPane())
      ),

      // Feedback
      buildMenuItem('Feedback_Bug', t('OfficeShellMenu.Feedback_Bug'), () => {
        renderFeedbackForm('Bug');
      }),
      buildMenuItem('Feedback_Like', t('OfficeShellMenu.Feedback_Like'), () => {
        renderFeedbackForm('Smile');
      }),
      buildMenuItem('Feedback_Suggestion', t('OfficeShellMenu.Feedback_Suggestion'), () => {
        renderFeedbackForm('Suggestion');
      }),
      // Divider
      {
        key: '_Divider',
        itemType: ContextualMenuItemType.Divider,
      },
    ],
    [dispatch, renderSubscriptionMenuItem, subscriptionLink, t]
  );

  const filteredMenuItems = useMemo(
    () =>
      (menuItemKeys ?? [])
        // map over menuItemKeys to respect the order
        .map((key) => menuItems.find((item) => item.key === key))
        .filter((item): item is MenuItem => !!item),
    [menuItemKeys, menuItems]
  );

  return (
    <ContextualMenu
      target={menuButtonId ? `#${menuButtonId}` : undefined}
      directionalHint={DirectionalHint.bottomRightEdge}
      items={filteredMenuItems}
      hidden={hidden}
      onItemClick={onHide}
      onDismiss={onHide}
      isBeakVisible={false}
    />
  );
};
